import { Fragment } from "react";
import { wertText, type Kennzahl, type KennzahlTon } from "./typen";

/** Statuspunkt am Label: Statusmittel des Blocks (surface-4 / grün / rot); „warn“ hat der Block nicht → neutral. */
const PUNKT_KLASSEN: Record<KennzahlTon, string> = {
  neutral: "size-2 inline-block bg-surface-4 rounded-full me-2",
  warn: "size-2 inline-block bg-surface-4 rounded-full me-2",
  ok: "size-2 inline-block bg-green-500 rounded-full me-2",
  risiko: "size-2 inline-block bg-red-500 rounded-full me-2",
};

/** Farbe der Veränderungs-Angabe: grün/rot wie im Block, sonst ungefärbt. */
const DELTA_KLASSEN: Record<KennzahlTon, string> = {
  neutral: "",
  warn: "",
  ok: "text-green-600",
  risiko: "text-red-600",
};

/**
 * Kandidat C: zentrierte Kachel, Statuspunkt am Label, Vergleichszeile.
 * Kann nicht: Aufschlüsselung, Absprung, Symbol, Ton „warn“, Trend „gleich“ als Glyph.
 * Vergleichszeile: Trend links („Veränderung“), Zusatz rechts. Das Raster ist für drei Kacheln ausgelegt.
 */
export function KennzahlKachelC({ kennzahlen }: { kennzahlen: Kennzahl[] }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
      {kennzahlen.map((k) => {
        const text = wertText(k);
        const ton = k.ton ?? "neutral";
        return (
          <Fragment key={k.label}>
            {/* Preline: data-display/data-display-sub-analytics-kpis/data-display-cat-kpi-cards/center-aligned-kpi-card */}
            <div className="flex flex-col gap-y-3 lg:gap-y-5 p-4 md:p-5 bg-card border border-card-line shadow-2xs rounded-xl">
              <div className="inline-flex justify-center items-center">
                <span className={PUNKT_KLASSEN[ton]}></span>
                <span className="text-xs font-semibold uppercase text-muted-foreground-2">{k.label}</span>
              </div>

              <div className="text-center">
                <h3 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground">
                  {text === null ? <span role="img" aria-label="nicht verfügbar">—</span> : text}
                </h3>
              </div>

              {(k.trend || k.zusatz) && (
                <dl className="flex justify-center items-center divide-x divide-line-2">
                  {k.trend && (
                    <dt className="pe-3">
                      <span className={DELTA_KLASSEN[ton]}>
                        {k.trend.richtung === "hoch" && (
                          <svg className="inline-block size-4 self-center" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                            <path fillRule="evenodd" d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z" />
                          </svg>
                        )}
                        {k.trend.richtung === "runter" && (
                          <svg className="inline-block size-4 self-center" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                            <path fillRule="evenodd" d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                          </svg>
                        )}
                        <span className="inline-block text-sm">{k.trend.text}</span>
                      </span>
                      <span className="block text-sm text-muted-foreground-1">Veränderung</span>
                    </dt>
                  )}
                  {k.zusatz && (
                    <dd className="text-start ps-3">
                      <span className="text-sm font-semibold text-foreground">{k.zusatz}</span>
                    </dd>
                  )}
                </dl>
              )}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}
